// Sprint INTADMIN INTADMIN.B2 (#2111) — pricing panel.
//
// Replaces B1's placeholder body. Route, layout, guard and sidebar entry all
// belong to #2110 and are untouched.
//
// Reads GET /api/internal/pricing and writes PUT /api/internal/pricing/:planKey
// (INTADMIN.A3, #2109). Scope is the PRD's editable set: USD price, INR price,
// INR mode, active flag. plan_key/pricing_model are immutable and Period is
// display-only; per-unit and BYO-LLM fields are a filed follow-on, not this
// story.
//
// ── The one thing to not get wrong (D1) ─────────────────────────────────────
//
// A live_rate row's INR is DERIVED for display and never persisted. #2109's GET
// returns the STORED base_price_inr verbatim and its PUT writes whatever it is
// sent, so putting the calculated figure in an editable input and saving would
// overwrite a deliberate PPP price with a conversion — Starter's ₹1,999 becomes
// ₹4,679 at 95.48 INR/USD, on a public page. buildPayload() omits inr_price
// entirely whenever the effective mode is live_rate; the PATCH shape leaves the
// stored value untouched.
//
// ── Edit state is raw strings (D2) ──────────────────────────────────────────
//
// A bare number cannot distinguish empty from zero from unchanged, and
// parseFloat('') is NaN. Each edited field is held as the raw input string, and
// the presence of the key IS the dirty flag. At save: '' -> null ("Contact us"),
// '0' -> 0 (Individual is genuinely free), absent -> omitted from the payload.
// Deliberately NOT copying EscalationOverridesSidebarCard's
// `if (!Number.isFinite(n)) return;` — that makes a field impossible to clear,
// and clearing to null is required here.
//
// ── Role gating is an affordance, not a gate (D5) ───────────────────────────
//
// #2110's shell admits any platform admin; #2109's mutations require
// SUPER_ADMIN. Without this a SUPPORT_ADMIN edits everything and learns on Save.
// The API's 403 remains the enforcement boundary.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, Check, ChevronRight, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { useInternalAdminRole } from '../../hooks/useInternalAdminRole';
import {
  formatPlanPricing,
  derivedInr,
  formatDerivedInr,
  num,
  INR_UNSET,
} from '../../lib/pricingDisplay';

// ── Types mirroring #2109's response ────────────────────────────────────────

type InrMode = 'ppp' | 'live_rate';

interface AdminPlan {
  key:            string;
  name:           string;
  pricing_model:  string;
  usd_price:      number | null;
  inr_price:      number | null;
  per_unit_usd:   number | null;
  per_unit_inr:   number | null;
  per_unit_label: string | null;
  byollm_usd:     number | null;
  byollm_inr:     number | null;
  byollm_note:    string | null;
  inr_mode:       InrMode;
  period:         string;
  is_active:      boolean;
  display_order:  number;
  // #2987 PR-B (migration 0254). Raw token counts; null = no published figure.
  allowance_tokens:          number | null;
  allowance_tokens_inr:      number | null;
  allowance_per_unit_tokens: number | null;
  updated_by:     string | null;
  updated_at:     string | null;
  created_at:     string | null;
}

interface RateBlock {
  live_rate:      number | null;
  source:         string;
  rate_cached_at: string | null;
  rate_stale:     boolean;
}

interface PricingResponse { plans: AdminPlan[]; rate: RateBlock }

interface RefreshResponse extends RateBlock {
  outcome: 'refreshed' | 'unchanged' | 'failed';
}

/** Presence of a key means "the user touched this field". */
interface RowEdit {
  usd_price?: string;
  inr_price?: string;
  inr_mode?:  InrMode;
  is_active?: boolean;
  // #2148 — the "Advanced pricing" detail panel. Same raw-string + presence
  // -is-the-dirty-flag shape as above (D2): a bare number cannot express
  // empty vs zero vs unchanged.
  per_unit_usd?:   string;
  per_unit_inr?:   string;
  per_unit_label?: string;
  byollm_usd?:     string;
  byollm_inr?:     string;
  byollm_note?:    string;
  // #2987 PR-B — monthly token allowances. Same raw-string shape.
  allowance_tokens?:          string;
  allowance_tokens_inr?:      string;
  allowance_per_unit_tokens?: string;
}

const ALLOWANCE_FIELDS = ['allowance_tokens', 'allowance_tokens_inr', 'allowance_per_unit_tokens'] as const;

/**
 * A token count as typed: digits only, and a safe integer. Empty means "no
 * published figure". Digits alone are not enough (AVR ruling 6283324252 item
 * 4): 309 nines become Infinity, which JSON.stringify sends as null, a silent
 * clear; 2^53 and up are rounded before they are sent.
 */
function isWholeTokenCount(raw: string): boolean {
  const t = raw.trim();
  return /^\d+$/.test(t) && Number.isSafeInteger(Number(t));
}

interface ApiError { status?: number; body?: { error?: string; code?: string; hint?: string; details?: { message?: string }[] } }

const LAST_ACTIVE_CONFIRM =
  'Deactivate the only remaining active plan?\n\n' +
  'With no active plans the public pricing endpoint returns an empty (but successful) ' +
  'response, so the website\'s fallback prices will NOT be used — blueprintsdlc.com/pricing ' +
  'would render with no prices at all.\n\n' +
  'The server will also reject this, so it is expected to fail.';

// ── Payload construction — the D1/D2/D3 rules in one place ──────────────────

export function buildPayload(plan: AdminPlan, edit: RowEdit): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  // The mode that will be in force after this save, which is what decides
  // whether INR is a stored value or a derived one. Reading the pending edit
  // first matters: a user who edits INR and then flips to Live Rate must not
  // have that now-meaningless edit persisted.
  const effectiveMode: InrMode = edit.inr_mode ?? plan.inr_mode;

  if (edit.usd_price !== undefined) {
    body.usd_price = edit.usd_price.trim() === '' ? null : Number(edit.usd_price);
  }

  // D1, extended by #2148 to ALL THREE INR fields.
  //
  // #2108 applies the same inrFor() derivation to inr_price, per_unit_inr AND
  // byollm_inr, with the same inr_mode and the same rate (routes/pricing.ts
  // :156/:158/:161). So in live_rate mode all three are computed from their
  // USD counterparts and the stored values are ignored — writing any of them
  // overwrites a deliberate PPP figure with a conversion that the public
  // endpoint will not even read.
  //
  // Gated as a group and unconditionally: the mode decides, not whether the
  // individual field happened to be touched this save. A user who edits an
  // INR field and then flips to Live Rate must not have that now-meaningless
  // edit persisted.
  if (effectiveMode === 'ppp') {
    if (edit.inr_price !== undefined) {
      body.inr_price = edit.inr_price.trim() === '' ? null : Number(edit.inr_price);
    }
    if (edit.per_unit_inr !== undefined) {
      body.per_unit_inr = edit.per_unit_inr.trim() === '' ? null : Number(edit.per_unit_inr);
    }
    if (edit.byollm_inr !== undefined) {
      body.byollm_inr = edit.byollm_inr.trim() === '' ? null : Number(edit.byollm_inr);
    }
  }

  // USD counterparts and the two text fields are mode-independent.
  if (edit.per_unit_usd !== undefined) {
    body.per_unit_usd = edit.per_unit_usd.trim() === '' ? null : Number(edit.per_unit_usd);
  }
  if (edit.byollm_usd !== undefined) {
    body.byollm_usd = edit.byollm_usd.trim() === '' ? null : Number(edit.byollm_usd);
  }
  // Text fields: empty clears to null (D2), which is what the coherence CHECK
  // requires for a 'flat' plan and rejects for per_seat/compound.
  if (edit.per_unit_label !== undefined) {
    body.per_unit_label = edit.per_unit_label.trim() === '' ? null : edit.per_unit_label;
  }
  if (edit.byollm_note !== undefined) {
    body.byollm_note = edit.byollm_note.trim() === '' ? null : edit.byollm_note;
  }

  // #2987 PR-B — token allowances, INR override included, are sent whatever
  // the INR mode. Unlike INR PRICES (D1 above), #2108 never derives an INR
  // allowance from the rate: it serves the stored value. Gating it by mode
  // would silently drop an admin's edit on every live_rate plan.
  for (const field of ALLOWANCE_FIELDS) {
    const raw = edit[field];
    if (raw !== undefined) body[field] = raw.trim() === '' ? null : Number(raw.trim());
  }

  if (edit.inr_mode  !== undefined) body.inr_mode  = edit.inr_mode;
  if (edit.is_active !== undefined) body.is_active = edit.is_active;

  return body;
}

/** The plan as it currently reads on screen — stored values plus pending edits. */
// EVERY editable field needs its own line here. `...plan` means a field left
// out silently reads the STORED value — the row renders as though nothing was
// typed, with no error anywhere. That reads as "the edit did not take" rather
// than as a bug, which is the worst kind to chase.
function effective(plan: AdminPlan, edit: RowEdit) {
  return {
    ...plan,
    usd_price: edit.usd_price !== undefined ? edit.usd_price : plan.usd_price,
    inr_price: edit.inr_price !== undefined ? edit.inr_price : plan.inr_price,
    inr_mode:  edit.inr_mode  ?? plan.inr_mode,
    is_active: edit.is_active ?? plan.is_active,
    per_unit_usd:   edit.per_unit_usd   !== undefined ? edit.per_unit_usd   : plan.per_unit_usd,
    per_unit_inr:   edit.per_unit_inr   !== undefined ? edit.per_unit_inr   : plan.per_unit_inr,
    per_unit_label: edit.per_unit_label !== undefined ? edit.per_unit_label : plan.per_unit_label,
    byollm_usd:     edit.byollm_usd     !== undefined ? edit.byollm_usd     : plan.byollm_usd,
    byollm_inr:     edit.byollm_inr     !== undefined ? edit.byollm_inr     : plan.byollm_inr,
    byollm_note:    edit.byollm_note    !== undefined ? edit.byollm_note    : plan.byollm_note,
    // #2987 PR-B's allowance fields are not here on purpose: nothing reads
    // them from `effective` (the inputs read the edit through fieldValue, and
    // the preview shows prices only). Add them if a preview ever shows one.
  };
}

function messageFor(err: unknown): string {
  const e = err as ApiError | null;
  const code = e?.body?.code;

  if (code === 'LAST_ACTIVE_PLAN') {
    return 'Rejected: at least one plan must stay active. The public pricing page would otherwise render with no prices.';
  }
  if (code === 'DISPLAY_ORDER_CONFLICT') {
    return 'Rejected: another plan already uses that display order.';
  }
  if (e?.status === 403) {
    return 'Your platform role cannot edit pricing. Your changes are still here — ask a SUPER_ADMIN to apply them.';
  }
  const details = e?.body?.details;
  if (details?.length) {
    return details.map(d => d.message).filter(Boolean).join('; ');
  }
  return e?.body?.error ?? `Save failed${e?.status ? ` (${e.status})` : ''}.`;
}

// ── Styling helpers — inline styles + CSS vars, per InternalAdminLayout ─────

const cellStyle: React.CSSProperties = {
  padding:      '10px 12px',
  borderBottom: '1px solid var(--border)',
  fontFamily:   'var(--font-body)',
  fontSize:     13,
  color:        'var(--text)',
  verticalAlign: 'top',
};

const headStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight:    600,
  fontSize:      11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color:         'var(--text-muted)',
  whiteSpace:    'nowrap',
};

const inputStyle: React.CSSProperties = {
  width:        90,
  padding:      '4px 6px',
  fontSize:     13,
  fontFamily:   'var(--font-body)',
  border:       '1px solid var(--border)',
  borderRadius: 4,
  background:   'var(--bg)',
  color:        'var(--text)',
};

const fieldLabelStyle: React.CSSProperties = {
  display:       'flex',
  flexDirection: 'column',
  gap:           4,
  fontSize:      11,
  fontFamily:    'var(--font-body)',
  color:         'var(--text-muted)',
};

const hintStyle: React.CSSProperties = {
  fontSize: 10,
  color:    'var(--text-muted)',
  maxWidth: 220,
};

function btnStyle(enabled: boolean): React.CSSProperties {
  return {
    padding:      '4px 10px',
    fontSize:     12,
    fontFamily:   'var(--font-body)',
    border:       '1px solid var(--border)',
    borderRadius: 4,
    background:   enabled ? 'var(--surface)' : 'transparent',
    color:        enabled ? 'var(--text)' : 'var(--text-muted)',
    cursor:       enabled ? 'pointer' : 'not-allowed',
    opacity:      enabled ? 1 : 0.55,
  };
}

// ── Component ───────────────────────────────────────────────────────────────

export default function InternalPricingPage() {
  const { canWrite, userId } = useInternalAdminRole();

  const [plans, setPlans]         = useState<AdminPlan[]>([]);
  const [rate, setRate]           = useState<RateBlock | null>(null);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState('');

  const [edits, setEdits]         = useState<Record<string, RowEdit>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [rowError, setRowError]   = useState<Record<string, string>>({});
  const [savedKey, setSavedKey]   = useState<string | null>(null);
  // #2148 — which rows have their "Advanced pricing" panel open. A Set, not a
  // single key: an operator filling the three missing INR values across
  // starter/growth/platform-partner wants them side by side, and per-row save
  // (D4) means open rows never interact.
  const [expanded, setExpanded]   = useState<Set<string>>(new Set());

  // #2147 — reorder is TABLE-level state, deliberately not part of `edits`.
  // Q7: one arrow click = one complete, valid ordering sent immediately. The
  // alternative (reorder locally, save explicitly) means holding a pending
  // order beside the per-row `edits` map — two independent dirty states in one
  // table, where an unsaved reorder is invisible in a way an unsaved number in
  // an input is not.
  //
  // `reordering` is a single flag rather than a per-row key because a reorder
  // moves TWO rows: keying it by the clicked plan would leave the other row's
  // arrows live during the request.
  const [reordering, setReordering]   = useState(false);
  const [reorderError, setReorderErr] = useState('');

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<{ tone: 'ok' | 'warn' | 'err'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PricingResponse>('/api/internal/pricing')
      .then(res => {
        if (cancelled) return;
        setPlans(res.plans ?? []);
        setRate(res.rate ?? null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(messageFor(err));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const setField = useCallback(<K extends keyof RowEdit>(key: string, field: K, value: RowEdit[K]) => {
    setEdits(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setSavedKey(null);
    setRowError(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
  }, []);

  const toggleExpanded = useCallback((key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const activeElsewhere = useCallback(
    (key: string) => plans.filter(p => p.key !== key && p.is_active).length,
    [plans],
  );

  const save = async (plan: AdminPlan) => {
    const edit = edits[plan.key] ?? {};
    const body = buildPayload(plan, edit);

    if (Object.keys(body).length === 0) return;

    // Reject unparseable input before it reaches the wire — Number('abc') is
    // NaN, which JSON.stringify turns into null and would silently CLEAR a
    // price rather than erroring.
    // #2148 — extended to the four new numeric fields. Number('abc') is NaN,
    // and JSON.stringify turns NaN into null, so an unvalidated typo would
    // reach the API as an explicit "clear this price" rather than an error.
    // The list must name every numeric field the panel can edit; a missing one
    // fails silently and destructively.
    for (const field of [
      'usd_price', 'inr_price',
      'per_unit_usd', 'per_unit_inr', 'byollm_usd', 'byollm_inr',
    ] as const) {
      const raw = edit[field];
      if (raw !== undefined && raw.trim() !== '' && num(raw) === null) {
        setRowError(prev => ({ ...prev, [plan.key]: `${field} must be a number, or empty for "Contact us".` }));
        return;
      }
    }

    // #2987 PR-B — an allowance is a whole number of tokens. Checked here so
    // a typo is named, rather than reaching the API as a fraction or NaN.
    for (const field of ALLOWANCE_FIELDS) {
      const raw = edit[field];
      if (raw !== undefined && raw.trim() !== '' && !isWholeTokenCount(raw)) {
        setRowError(prev => ({ ...prev, [plan.key]: `${field} must be a whole number of tokens, or empty for no published figure.` }));
        return;
      }
    }

    // Client-side affordance only — #2109 enforces this server-side inside a
    // rolled-back transaction and returns 409 LAST_ACTIVE_PLAN regardless.
    if (body.is_active === false && activeElsewhere(plan.key) === 0) {
      if (!window.confirm(LAST_ACTIVE_CONFIRM)) return;
    }

    setSavingKey(plan.key);
    setRowError(prev => ({ ...prev, [plan.key]: '' }));

    try {
      const updated = await apiFetch<AdminPlan>(`/api/internal/pricing/${encodeURIComponent(plan.key)}`, {
        method: 'PUT',
        body:   JSON.stringify(body),
      });
      setPlans(prev => prev.map(p => (p.key === plan.key ? updated : p)));
      // Only clear this row's edits, and only on success — every failure path
      // below leaves them untouched so nothing typed is lost.
      setEdits(prev => {
        const next = { ...prev };
        delete next[plan.key];
        return next;
      });
      setSavedKey(plan.key);
    } catch (err) {
      setRowError(prev => ({ ...prev, [plan.key]: messageFor(err) }));
    } finally {
      setSavingKey(null);
    }
  };

  // #2147 — move one plan one position. `delta` is -1 (up) or +1 (down).
  //
  // Sends the FULL ordered key array, never {key, position} pairs: display_order
  // is UNIQUE across all rows, so a payload naming only the two moved plans is a
  // partial payload, which the server rejects — and which, before that guard
  // existed, silently stranded every unnamed plan below zero and reversed the
  // public pricing page (recon probe E).
  const move = async (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= plans.length) return;

    const order = plans.map(p => p.key);
    [order[index], order[target]] = [order[target], order[index]];

    setReordering(true);
    setReorderErr('');
    try {
      const res = await apiFetch<{ plans: AdminPlan[] }>('/api/internal/pricing/order', {
        method: 'PUT',
        body:   JSON.stringify({ order }),
      });
      // NOT optimistic, deliberately. The server is what assigns positions, and
      // a rejected reorder would have to visibly snap back — worse than a brief
      // disabled state. `plans` is replaced only from the response.
      //
      // `edits` is intentionally left alone: it is keyed by plan.key, so pending
      // per-row price edits survive this replacement. Q8 — they are independent
      // server-side, and silently discarding them here would lose typed input.
      // Pinned by a test; it breaks if anyone re-keys `edits` by index.
      setPlans(res.plans);
    } catch (err) {
      setReorderErr(messageFor(err));
    } finally {
      setReordering(false);
    }
  };

  const refreshRate = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const res = await apiFetch<RefreshResponse>('/api/internal/pricing/rate/refresh', { method: 'POST' });
      setRate({
        live_rate:      res.live_rate,
        source:         res.source,
        rate_cached_at: res.rate_cached_at,
        rate_stale:     res.rate_stale,
      });
      // D9 — three distinct outcomes. "unchanged" is a success, not a failure:
      // the provider was reached and confirmed the same number.
      setRefreshMsg(
        res.outcome === 'refreshed'
          ? { tone: 'ok',   text: 'Rate updated.' }
          : { tone: 'ok',   text: 'Rate re-checked, unchanged.' },
      );
    } catch (err) {
      // 502 means the PROVIDER failed. The previously cached rate is still
      // valid and still on screen — collapsing this into a generic error would
      // suggest the displayed number is now untrustworthy.
      const e = err as ApiError & { body?: RefreshResponse };
      if (e?.status === 502 && e.body) {
        setRate({
          live_rate:      e.body.live_rate,
          source:         e.body.source,
          rate_cached_at: e.body.rate_cached_at,
          rate_stale:     e.body.rate_stale,
        });
        setRefreshMsg({ tone: 'warn', text: 'Provider unavailable — showing last known rate.' });
      } else {
        setRefreshMsg({ tone: 'err', text: messageFor(err) });
      }
    } finally {
      setRefreshing(false);
    }
  };

  const previews = useMemo(
    () =>
      plans.map(plan => {
        const eff = effective(plan, edits[plan.key] ?? {});
        // The public page derives INR for live_rate plans (#2108's inrFor), so
        // the preview must too — it is showing what the website will render,
        // not what the database holds.
        const inrForPreview =
          eff.inr_mode === 'live_rate' ? derivedInr(eff.usd_price, rate?.live_rate ?? null) : eff.inr_price;
        return {
          key:      plan.key,
          name:     plan.name,
          active:   eff.is_active,
          lines:    formatPlanPricing({ ...eff, inr_price: inrForPreview }),
        };
      }),
    [plans, edits, rate],
  );

  if (loading) {
    return <div data-testid="internal-pricing-loading" style={{ ...cellStyle, borderBottom: 'none' }}>Loading pricing…</div>;
  }

  return (
    <div data-testid="internal-pricing-panel">
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
        Pricing
      </h1>

      {loadError && (
        <div data-testid="internal-pricing-load-error" style={{ padding: '8px 12px', marginBottom: 12, background: 'rgba(239,68,68,0.08)', color: 'var(--error)', borderRadius: 4, fontSize: 13 }}>
          {loadError}
        </div>
      )}

      {!canWrite && (
        <div data-testid="pricing-readonly-notice" style={{ padding: '8px 12px', marginBottom: 12, background: 'rgba(245,158,11,0.10)', color: 'var(--text)', borderRadius: 4, fontSize: 13 }}>
          Read-only — editing pricing requires the SUPER_ADMIN platform role.
        </div>
      )}

      {/* ── Exchange rate header ─────────────────────────────────────────── */}
      {rate && (
        <div data-testid="pricing-rate-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', marginBottom: 12, border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)', color: 'var(--text)' }}>
          <span data-testid="pricing-rate-value">
            USD→INR: <strong>{rate.live_rate === null ? 'unavailable' : rate.live_rate}</strong>
          </span>
          <span style={{ color: 'var(--text-muted)' }} data-testid="pricing-rate-source">
            source: {rate.source}
          </span>
          <span style={{ color: 'var(--text-muted)' }} data-testid="pricing-rate-cached-at">
            cached: {rate.rate_cached_at ?? 'never'}
          </span>
          <button
            type="button"
            onClick={refreshRate}
            disabled={!canWrite || refreshing}
            data-testid="pricing-rate-refresh"
            title={canWrite ? 'Force a fresh fetch from the provider' : 'Requires the SUPER_ADMIN platform role'}
            style={btnStyle(canWrite && !refreshing)}
          >
            <RefreshCw size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
            {refreshing ? 'Refreshing…' : 'Refresh rate'}
          </button>
          {refreshMsg && (
            <span
              data-testid="pricing-rate-message"
              style={{ color: refreshMsg.tone === 'ok' ? 'var(--success)' : refreshMsg.tone === 'warn' ? 'var(--warning)' : 'var(--error)' }}
            >
              {refreshMsg.text}
            </span>
          )}
        </div>
      )}

      {rate?.rate_stale && (
        <div data-testid="pricing-rate-stale-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 12, background: 'rgba(245,158,11,0.12)', borderRadius: 4, fontSize: 13 }}>
          <AlertTriangle size={14} />
          Exchange rate is stale — the last refresh did not succeed. Live-rate plans are using the cached figure.
        </div>
      )}

      {/* ── Plans table ──────────────────────────────────────────────────── */}
      {/* #2147 — reorder failures are TABLE-level, not row-level: a reorder that
          fails failed for the whole ordering, and attributing it to the clicked
          row would misdescribe it. */}
      {reorderError && (
        <div data-testid="pricing-reorder-error" style={{ marginBottom: 12, color: 'var(--error)', fontSize: 13 }}>
          <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
          {reorderError}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 28 }} data-testid="pricing-table">
        <thead>
          <tr>
            <th style={headStyle}>Plan</th>
            <th style={headStyle}>USD price</th>
            <th style={headStyle}>INR price</th>
            <th style={headStyle}>INR mode</th>
            <th style={headStyle}>Period</th>
            <th style={headStyle}>Active</th>
            <th style={headStyle}>Updated by</th>
            <th style={headStyle}>Updated at</th>
            <th style={headStyle} />
          </tr>
        </thead>
        <tbody>
          {plans.map((plan, rowIndex) => {
            const edit = edits[plan.key] ?? {};
            const eff  = effective(plan, edit);
            const mode = eff.inr_mode;
            const dirty = Object.keys(edit).length > 0;
            const busy  = savingKey === plan.key;

            const usdValue = edit.usd_price !== undefined ? edit.usd_price : (plan.usd_price ?? '');
            const inrValue = edit.inr_price !== undefined ? edit.inr_price : (plan.inr_price ?? '');

            // #2148 — per-unit AMOUNTS are legal only on a compound plan.
            // 0207's coherence CHECK requires per_unit_usd and per_unit_inr to be
            // NULL for both 'flat' and 'per_seat', and pricing_model is immutable,
            // so a row can never move between those buckets. Rendering the inputs
            // anyway would offer controls that can only ever produce a 400.
            const isCompound = plan.pricing_model === 'compound';
            // The LABEL is required for per_seat and compound, forbidden for flat.
            const wantsUnitLabel = isCompound || plan.pricing_model === 'per_seat';
            const isOpen = expanded.has(plan.key);

            const fieldValue = (k: 'per_unit_usd' | 'per_unit_inr' | 'per_unit_label' | 'byollm_usd' | 'byollm_inr' | 'byollm_note' | typeof ALLOWANCE_FIELDS[number]) =>
              edit[k] !== undefined ? edit[k] : (plan[k] ?? '');

            const row = (
              <tr key={plan.key} data-testid={`pricing-row-${plan.key}`}>
                <td style={cellStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(plan.key)}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Hide' : 'Show'} advanced pricing for ${plan.name}`}
                      data-testid={`pricing-expand-${plan.key}`}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'var(--text-muted)', lineHeight: 1 }}
                    >
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <div style={{ fontWeight: 600 }}>{plan.name}</div>
                  </div>
                  {/* Immutable — display-only, no control. */}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {plan.key} · {plan.pricing_model}
                  </div>
                </td>

                <td style={cellStyle}>
                  <input
                    type="number"
                    step="0.01"
                    value={String(usdValue)}
                    onChange={e => setField(plan.key, 'usd_price', e.target.value)}
                    disabled={!canWrite || busy}
                    placeholder="Contact us"
                    aria-label={`USD price for ${plan.name}`}
                    data-testid={`pricing-usd-${plan.key}`}
                    style={inputStyle}
                  />
                </td>

                <td style={cellStyle}>
                  {mode === 'live_rate' ? (
                    // D1 — derived, read-only, and NOT an input. Rendering this
                    // inside the editable field is what would let a save write
                    // a conversion over the stored PPP price.
                    <span
                      data-testid={`pricing-inr-derived-${plan.key}`}
                      title="Calculated from USD at the current live rate. Not stored."
                      style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}
                    >
                      {formatDerivedInr(derivedInr(eff.usd_price, rate?.live_rate ?? null))}
                      <span style={{ fontSize: 10, display: 'block' }}>calculated</span>
                    </span>
                  ) : (
                    <input
                      type="number"
                      step="0.01"
                      value={String(inrValue)}
                      onChange={e => setField(plan.key, 'inr_price', e.target.value)}
                      disabled={!canWrite || busy}
                      placeholder={INR_UNSET}
                      aria-label={`INR price for ${plan.name}`}
                      data-testid={`pricing-inr-${plan.key}`}
                      style={inputStyle}
                    />
                  )}
                </td>

                <td style={cellStyle}>
                  <button
                    type="button"
                    onClick={() => setField(plan.key, 'inr_mode', mode === 'ppp' ? 'live_rate' : 'ppp')}
                    disabled={!canWrite || busy}
                    aria-label={`INR mode for ${plan.name}: currently ${mode === 'ppp' ? 'PPP' : 'Live Rate'}`}
                    data-testid={`pricing-mode-${plan.key}`}
                    style={btnStyle(canWrite && !busy)}
                  >
                    {mode === 'ppp' ? 'PPP' : 'Live Rate'}
                  </button>
                </td>

                <td style={{ ...cellStyle, color: 'var(--text-muted)' }} data-testid={`pricing-period-${plan.key}`}>
                  {plan.period}
                </td>

                <td style={cellStyle}>
                  <input
                    type="checkbox"
                    checked={eff.is_active}
                    onChange={e => setField(plan.key, 'is_active', e.target.checked)}
                    disabled={!canWrite || busy}
                    aria-label={`${plan.name} visible on the public pricing page`}
                    data-testid={`pricing-active-${plan.key}`}
                  />
                </td>

                <td style={{ ...cellStyle, fontSize: 11, color: 'var(--text-muted)' }} data-testid={`pricing-updated-by-${plan.key}`}>
                  {/* D7 — never a bare full UUID as visible text. */}
                  {plan.updated_by === null
                    ? '—'
                    : plan.updated_by === userId
                      ? 'You'
                      : <span title={plan.updated_by}>{plan.updated_by.slice(0, 8)}…</span>}
                </td>

                <td style={{ ...cellStyle, fontSize: 11, color: 'var(--text-muted)' }} data-testid={`pricing-updated-at-${plan.key}`}>
                  {plan.updated_at ?? '—'}
                </td>

                <td style={cellStyle}>
                  {/* #2147 — reorder arrows.
                      In the EXISTING trailing actions cell, not a new column:
                      the row is 9 <td>s and #2148's detail row spans colSpan={9},
                      so a 10th column would force that to 10 as well. Recon §4/§8.

                      Arrows rather than drag-and-drop (Q6): @dnd-kit IS already a
                      dependency, so this is not a cost argument — it is that the
                      repo's only dnd precedent (BoardPage.test.tsx:36-44) mocks the
                      library out of its own tests, so the drag interaction there is
                      untested. Shipping the riskiest behaviour of a story whose
                      failure mode is a corrupted public price order with no coverage
                      is the wrong trade for five rows. Arrows are also
                      keyboard-accessible with no extra work. */}
                  <span style={{ display: 'inline-flex', gap: 2, marginRight: 6, verticalAlign: 'middle' }}>
                    <button
                      type="button"
                      onClick={() => void move(rowIndex, -1)}
                      // Disabled across ALL rows while any reorder is in flight —
                      // a second click mid-request would compute its array from
                      // the pre-move `plans` and undo the first.
                      disabled={!canWrite || reordering || rowIndex === 0}
                      aria-label={`Move ${plan.name} up`}
                      data-testid={`pricing-move-up-${plan.key}`}
                      style={btnStyle(canWrite && !reordering && rowIndex !== 0)}
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void move(rowIndex, 1)}
                      disabled={!canWrite || reordering || rowIndex === plans.length - 1}
                      aria-label={`Move ${plan.name} down`}
                      data-testid={`pricing-move-down-${plan.key}`}
                      style={btnStyle(canWrite && !reordering && rowIndex !== plans.length - 1)}
                    >
                      <ArrowDown size={12} />
                    </button>
                  </span>
                  <button
                    type="button"
                    onClick={() => void save(plan)}
                    disabled={!canWrite || busy || !dirty}
                    aria-label={`Save changes to ${plan.name}`}
                    data-testid={`pricing-save-${plan.key}`}
                    style={btnStyle(canWrite && !busy && dirty)}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  {savedKey === plan.key && (
                    <span data-testid={`pricing-saved-${plan.key}`} style={{ marginLeft: 6, color: 'var(--success)', fontSize: 12 }}>
                      <Check size={12} style={{ verticalAlign: -2 }} /> Saved
                    </span>
                  )}
                  {rowError[plan.key] && (
                    <div data-testid={`pricing-error-${plan.key}`} style={{ marginTop: 4, color: 'var(--error)', fontSize: 12, maxWidth: 260 }}>
                      {rowError[plan.key]}
                    </div>
                  )}
                </td>
              </tr>
            );

            const detailRow = isOpen ? (
              <tr key={`${plan.key}-advanced`} data-testid={`pricing-advanced-${plan.key}`}>
                <td colSpan={9} style={{ ...cellStyle, background: 'var(--surface)' }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>
                    Advanced pricing — {plan.name}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>

                    {isCompound && (
                      <>
                        <label style={fieldLabelStyle}>
                          Per-unit USD
                          <input
                            type="number"
                            step="0.01"
                            value={String(fieldValue('per_unit_usd'))}
                            onChange={e => setField(plan.key, 'per_unit_usd', e.target.value)}
                            disabled={!canWrite || busy}
                            aria-label={`Per-unit USD price for ${plan.name}`}
                            data-testid={`pricing-per-unit-usd-${plan.key}`}
                            style={inputStyle}
                          />
                        </label>

                        <label style={fieldLabelStyle}>
                          Per-unit INR
                          {mode === 'live_rate' ? (
                            <span
                              data-testid={`pricing-per-unit-inr-derived-${plan.key}`}
                              title="Calculated from USD at the current live rate. Not stored."
                              style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 13 }}
                            >
                              {formatDerivedInr(derivedInr(fieldValue('per_unit_usd'), rate?.live_rate ?? null))}
                              <span style={{ fontSize: 10, display: 'block' }}>calculated</span>
                            </span>
                          ) : (
                            <input
                              type="number"
                              step="0.01"
                              value={String(fieldValue('per_unit_inr'))}
                              onChange={e => setField(plan.key, 'per_unit_inr', e.target.value)}
                              disabled={!canWrite || busy}
                              placeholder={INR_UNSET}
                              aria-label={`Per-unit INR price for ${plan.name}`}
                              data-testid={`pricing-per-unit-inr-${plan.key}`}
                              style={inputStyle}
                            />
                          )}
                        </label>
                      </>
                    )}

                    {wantsUnitLabel && (
                      <label style={fieldLabelStyle}>
                        Unit label
                        <input
                          type="text"
                          maxLength={30}
                          value={String(fieldValue('per_unit_label'))}
                          onChange={e => setField(plan.key, 'per_unit_label', e.target.value)}
                          disabled={!canWrite || busy}
                          aria-label={`Unit label for ${plan.name}`}
                          data-testid={`pricing-per-unit-label-${plan.key}`}
                          style={inputStyle}
                        />
                        <span style={hintStyle}>Appears on the public pricing page.</span>
                      </label>
                    )}

                    <label style={fieldLabelStyle}>
                      BYO-LLM USD
                      <input
                        type="number"
                        step="0.01"
                        value={String(fieldValue('byollm_usd'))}
                        onChange={e => setField(plan.key, 'byollm_usd', e.target.value)}
                        disabled={!canWrite || busy}
                        aria-label={`BYO-LLM USD price for ${plan.name}`}
                        data-testid={`pricing-byollm-usd-${plan.key}`}
                        style={inputStyle}
                      />
                    </label>

                    <label style={fieldLabelStyle}>
                      BYO-LLM INR
                      {mode === 'live_rate' ? (
                        <span
                          data-testid={`pricing-byollm-inr-derived-${plan.key}`}
                          title="Calculated from USD at the current live rate. Not stored."
                          style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 13 }}
                        >
                          {formatDerivedInr(derivedInr(fieldValue('byollm_usd'), rate?.live_rate ?? null))}
                          <span style={{ fontSize: 10, display: 'block' }}>calculated</span>
                        </span>
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          value={String(fieldValue('byollm_inr'))}
                          onChange={e => setField(plan.key, 'byollm_inr', e.target.value)}
                          disabled={!canWrite || busy}
                          placeholder={INR_UNSET}
                          aria-label={`BYO-LLM INR price for ${plan.name}`}
                          data-testid={`pricing-byollm-inr-${plan.key}`}
                          style={inputStyle}
                        />
                      )}
                    </label>

                    <label style={{ ...fieldLabelStyle, flex: '1 1 240px' }}>
                      BYO-LLM note
                      <input
                        type="text"
                        maxLength={500}
                        value={String(fieldValue('byollm_note'))}
                        onChange={e => setField(plan.key, 'byollm_note', e.target.value)}
                        disabled={!canWrite || busy}
                        aria-label={`BYO-LLM note for ${plan.name}`}
                        data-testid={`pricing-byollm-note-${plan.key}`}
                        style={{ ...inputStyle, width: '100%' }}
                      />
                      {/* Both-present is legal and now reachable. #2111 renders the
                          number as the primary line and the note beneath it, so say
                          so rather than letting the note look ignored. */}
                      {String(fieldValue('byollm_usd')) !== '' && String(fieldValue('byollm_note')) !== '' && (
                        <span data-testid={`pricing-byollm-both-${plan.key}`} style={hintStyle}>
                          Shown beneath the BYO-LLM price, not instead of it.
                        </span>
                      )}
                    </label>

                    {/* #2987 PR-B — monthly token allowances (migration 0254).
                        Text inputs, not type=number: a browser number input
                        sanitises "1.5x" to "" silently, which would read as
                        a deliberate "no published figure". */}
                    <label style={fieldLabelStyle}>
                      {isCompound ? 'Allowance, base (tokens / month)' : plan.pricing_model === 'per_seat' ? 'Allowance (tokens / seat / month)' : 'Allowance (tokens / month)'}
                      <input
                        type="text"
                        inputMode="numeric"
                        value={String(fieldValue('allowance_tokens'))}
                        onChange={e => setField(plan.key, 'allowance_tokens', e.target.value)}
                        disabled={!canWrite || busy}
                        placeholder="Custom"
                        aria-label={`Monthly token allowance for ${plan.name}`}
                        data-testid={`pricing-allowance-${plan.key}`}
                        style={{ ...inputStyle, width: 110 }}
                      />
                      <span style={hintStyle}>Empty: no published figure.</span>
                    </label>

                    <label style={fieldLabelStyle}>
                      Allowance, INR billing
                      <input
                        type="text"
                        inputMode="numeric"
                        value={String(fieldValue('allowance_tokens_inr'))}
                        onChange={e => setField(plan.key, 'allowance_tokens_inr', e.target.value)}
                        disabled={!canWrite || busy}
                        placeholder="Same as USD"
                        aria-label={`INR-billing token allowance for ${plan.name}`}
                        data-testid={`pricing-allowance-inr-${plan.key}`}
                        style={{ ...inputStyle, width: 110 }}
                      />
                      <span style={hintStyle}>Stored as typed, in either INR mode.</span>
                    </label>

                    {isCompound && (
                      <label style={fieldLabelStyle}>
                        Allowance per {plan.per_unit_label ?? 'unit'} (tokens / month)
                        <input
                          type="text"
                          inputMode="numeric"
                          value={String(fieldValue('allowance_per_unit_tokens'))}
                          onChange={e => setField(plan.key, 'allowance_per_unit_tokens', e.target.value)}
                          disabled={!canWrite || busy}
                          aria-label={`Per-${plan.per_unit_label ?? 'unit'} token allowance for ${plan.name}`}
                          data-testid={`pricing-allowance-per-unit-${plan.key}`}
                          style={{ ...inputStyle, width: 110 }}
                        />
                      </label>
                    )}

                  </div>
                </td>
              </tr>
            ) : null;

            return detailRow ? [row, detailRow] : row;
          })}
        </tbody>
      </table>

      {/* ── Live preview ─────────────────────────────────────────────────── */}
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        Website preview
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        How the current values — including unsaved edits — will read on blueprintsdlc.com/pricing.
      </p>

      <div data-testid="pricing-preview" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {previews.map(p => (
          <div
            key={p.key}
            data-testid={`preview-${p.key}`}
            style={{ flex: '1 1 200px', minWidth: 200, padding: 12, border: '1px solid var(--border)', borderRadius: 6, opacity: p.active ? 1 : 0.45 }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
              {p.name}{!p.active && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> (hidden)</span>}
            </div>
            <div data-testid={`preview-usd-${p.key}`} style={{ fontSize: 14, fontWeight: 600 }}>{p.lines.usd}</div>
            {p.lines.usdPerUnit && (
              <div data-testid={`preview-usd-unit-${p.key}`} style={{ fontSize: 12 }}>+ {p.lines.usdPerUnit}</div>
            )}
            <div data-testid={`preview-inr-${p.key}`} style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{p.lines.inr}</div>
            {p.lines.inrPerUnit && (
              <div data-testid={`preview-inr-unit-${p.key}`} style={{ fontSize: 12, color: 'var(--text-muted)' }}>+ {p.lines.inrPerUnit}</div>
            )}
            {p.lines.byollm && (
              <div data-testid={`preview-byollm-${p.key}`} style={{ fontSize: 11, marginTop: 6, color: 'var(--text-muted)' }}>{p.lines.byollm}</div>
            )}
            {/* #2148 — a note set ALONGSIDE a price renders beneath it rather
                than being discarded. Reachable now that both are editable. */}
            {p.lines.byollmNote && (
              <div data-testid={`preview-byollm-note-${p.key}`} style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>{p.lines.byollmNote}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
